/**
 * Recovery Code Notification Utilities
 *
 * The backup_code AMR value persists for the lifetime of the Sirius browser
 * session, and every silent re-auth issues a fresh ID token carrying it, so
 * deduplication uses a timestamp with a 24-hour TTL.
 */

import { decodeJwtPayload } from './mfa-notification.utils';

const RECOVERY_CODE_NOTIFICATION_SHOWN_KEY =
  'recovery_code_notification_shown_at';

const RECOVERY_CODE_NOTIFICATION_TTL_MS = 24 * 60 * 60 * 1000;

function recoveryCodeNotificationKey(
  claims: Record<string, unknown> | null
): string {
  const subject = claims?.['sub'];

  return typeof subject === 'string' && subject
    ? `${RECOVERY_CODE_NOTIFICATION_SHOWN_KEY}:${subject}`
    : RECOVERY_CODE_NOTIFICATION_SHOWN_KEY;
}

export function shouldShowRecoveryCodeNotification(
  idToken: string | null
): boolean {
  if (!idToken) {
    return false;
  }

  const claims = decodeJwtPayload(idToken);

  const amr = claims?.['amr'];
  if (!Array.isArray(amr) || !amr.includes('backup_code')) {
    return false;
  }

  const shownAt = localStorage.getItem(recoveryCodeNotificationKey(claims));
  const cutoff = Date.now() - RECOVERY_CODE_NOTIFICATION_TTL_MS;

  if (shownAt && parseInt(shownAt, 10) > cutoff) {
    return false;
  }

  return true;
}

export function markRecoveryCodeNotificationShown(
  idToken: string | null
): void {
  const claims = idToken ? decodeJwtPayload(idToken) : null;

  localStorage.setItem(
    recoveryCodeNotificationKey(claims),
    Date.now().toString()
  );
}

export function getRecoveryCodeNotificationTTL(): number {
  return RECOVERY_CODE_NOTIFICATION_TTL_MS;
}
